import {
    getApiError,
    getTaskListsThunk,
    getUserTeamGetByManagerThunk,
    getWorkGetScheduleThunk,
    getWorkStartAndGetScheduleThunk,
} from "tm-shared/api";
import { UserRole } from "tm-shared/types";

import { useInitRetryRequests } from "@/features/appInit/hooks/useInitRetryRequests";
import { useAsyncAction, useAsyncTaskQueue } from "@/hooks";
import {
    useAppDispatch,
    useAppSelector,
    useOfflineModeSelector,
} from "@/hooks/redux";
import { setSVActiveSchedule } from "@/store/slices/manager";
import {
    getKpiGetPointProgressesThunk,
    getPlansByPointIdThunk,
    setIsInitialDataLoading,
} from "@/store/slices/offline";
import { resetUiState, setInitFailedRequests } from "@/store/slices/ui";

import { sendUserLocation } from "../utils/locationUtils";

export const useLoadUserSchedule = () => {
    const dispatch = useAppDispatch();
    const offlineMode = useOfflineModeSelector();
    const initFailedRequests = useAppSelector((s) => s.ui.initFailedRequests);
    const managerId = useAppSelector((state) => state.user.id);

    const { queue, progress: queueProgress } = useAsyncTaskQueue();
    const initRequests = useInitRetryRequests();

    const [handleLoadUserSchedule, isProcessing] = useAsyncAction(
        async (userId: string, roleSlug: UserRole, routeId: string) => {
            queue.clear();

            let scheduleLoaded = false;

            queue.add("Завантаження розкладу роботи", async () => {
                try {
                    const { result } = await dispatch(
                        getWorkStartAndGetScheduleThunk({ userId, roleSlug }),
                    ).unwrap();

                    scheduleLoaded = true;

                    await dispatch(getUserTeamGetByManagerThunk({ managerId }));

                    const pointIds = Object.keys(result?.points ?? {});

                    if (pointIds.length) {
                        await loadAdditionalData(pointIds, roleSlug, routeId);
                    }
                } catch (error) {
                    const { code } = getApiError(error);

                    if (code === "-32400") {
                        await handleScheduleError(userId, roleSlug, routeId);
                        scheduleLoaded = true;
                        return;
                    }

                    throw error;
                }
            });

            initFailedRequests.forEach((type) => {
                const cmd = initRequests[type];
                if (!cmd) return;
                queue.add(cmd.title, cmd.run);
            });

            try {
                await queue.runParallel();
            } finally {
                if (scheduleLoaded) {
                    dispatch(resetUiState());
                    dispatch(setSVActiveSchedule({ userId, routeId }));
                }

                dispatch(setIsInitialDataLoading(false));
                dispatch(setInitFailedRequests([]));

                if (!offlineMode) {
                    sendUserLocation();
                }
            }
        },
    );

    const loadAdditionalData = async (
        pointIds: string[],
        roleSlug: string,
        routeId?: string,
    ) => {
        await queue.add("Завантаження інформації про задачі", () =>
            dispatch(
                getTaskListsThunk({
                    targetIds: pointIds,
                    roleSlug,
                }),
            ).unwrap(),
        );

        if (roleSlug === UserRole.Ta && routeId) {
            await queue.add(
                "Завантаження інформації про плани на ТТ",
                async () => {
                    await dispatch(
                        getPlansByPointIdThunk({
                            pointIds,
                            routeId,
                        }),
                    );
                    queue.add("Завантаження інформації про факти на ТТ", () =>
                        dispatch(
                            getKpiGetPointProgressesThunk({
                                pointIds,
                            }),
                        ),
                    );
                },
            );
        }
    };

    const handleScheduleError = async (
        userId: string,
        roleSlug: UserRole,
        routeId?: string,
    ) => {
        const { result } = await dispatch(
            getWorkGetScheduleThunk({ userId, roleSlug }),
        ).unwrap();

        const pointIds = Object.keys(result?.points ?? {});

        if (pointIds.length) {
            await loadAdditionalData(pointIds, roleSlug, routeId);
        }
    };

    return {
        handleLoadUserSchedule,
        isProcessing,
        queueProgress,
    };
};
